import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
    Plus, X, Search, Filter, Tag, Pencil, Trash2, MoreVertical,
    Calendar, MessageSquare, CheckSquare, Paperclip, Building2,
    User as UserIcon, GripVertical, Palette,
} from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuSeparator, DropdownMenuLabel,
} from '../../components/ui/dropdown-menu';
import { Popover, PopoverTrigger, PopoverContent } from '../../components/ui/popover';
import CardModal from './CardModal';

const PRIORITY_META = {
    low:     { label: 'Low',    dot: '#94a3b8' },
    medium:  { label: 'Medium', dot: '#3b82f6' },
    high:    { label: 'High',   dot: '#f59e0b' },
    urgent:  { label: 'Urgent', dot: '#dc2626' },
};

const SWATCHES = [
    '#94a3b8', '#64748b', '#475569',          // slates
    '#ef4444', '#dc2626', '#f97316', '#f59e0b', // warm
    '#10b981', '#059669', '#14b8a6',          // greens
    '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', // blues / purples
    '#ec4899', '#f43f5e',                     // pinks
];

// Generates a new fractional order so we can insert between two siblings
// without re-numbering the whole list.
function computeNewOrder(siblings, destIndex) {
    if (siblings.length === 0) return 1024;
    if (destIndex <= 0) return (siblings[0].order || 0) - 1024;
    if (destIndex >= siblings.length) return (siblings[siblings.length - 1].order || 0) + 1024;
    const prev = siblings[destIndex - 1].order || 0;
    const next = siblings[destIndex].order || 0;
    return (prev + next) / 2;
}

export default function BacklogBoard() {
    const { user, can } = useAuth();
    const canEdit   = can('projects', 'edit');
    const canCreate = can('projects', 'create');
    const canDelete = can('projects', 'delete');

    const [data, setData]         = useState(null);   // { board, cards, clients, users }
    const [loading, setLoading]   = useState(true);
    const [search, setSearch]     = useState('');
    const [clientFilter, setClientFilter] = useState([]);   // client ids
    const [labelFilter, setLabelFilter]   = useState([]);   // label ids
    const [priorityFilter, setPriorityFilter] = useState([]);
    const [assigneeFilter, setAssigneeFilter] = useState([]);
    const [openCardId, setOpenCardId]     = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await api.get('/backlog/board');
            setData(r.data);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Failed to load board');
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    // Build filtered, grouped card lists per column
    const grouped = useMemo(() => {
        if (!data) return {};
        const q = search.trim().toLowerCase();
        const cf = new Set(clientFilter);
        const lf = new Set(labelFilter);
        const pf = new Set(priorityFilter);
        const af = new Set(assigneeFilter);
        const out = {};
        for (const col of data.board.columns) out[col.id] = [];
        for (const c of data.cards) {
            if (q && !(c.title || '').toLowerCase().includes(q) && !(c.description || '').toLowerCase().includes(q)) continue;
            if (cf.size && !c.client_ids.some(id => cf.has(id))) continue;
            if (lf.size && !c.label_ids.some(id => lf.has(id))) continue;
            if (pf.size && !pf.has(c.priority)) continue;
            if (af.size && !c.assignee_ids.some(id => af.has(id))) continue;
            if (out[c.column_id]) out[c.column_id].push(c);
        }
        for (const k of Object.keys(out)) out[k].sort((a, b) => (a.order || 0) - (b.order || 0));
        return out;
    }, [data, search, clientFilter, labelFilter, priorityFilter, assigneeFilter]);

    const labelById  = useMemo(() => Object.fromEntries((data?.board.labels || []).map(l => [l.id, l])), [data]);
    const clientById = useMemo(() => Object.fromEntries((data?.clients || []).map(c => [c.id, c])), [data]);
    const userById   = useMemo(() => Object.fromEntries((data?.users  || []).map(u => [u.id, u])), [data]);

    const filtersActive = clientFilter.length || labelFilter.length || priorityFilter.length || assigneeFilter.length || search;

    const onDragEnd = async (result) => {
        const { destination, source, type, draggableId } = result;
        if (!destination) return;

        if (type === 'COLUMN') {
            if (destination.index === source.index) return;
            const newCols = [...data.board.columns];
            const [moved] = newCols.splice(source.index, 1);
            newCols.splice(destination.index, 0, moved);
            setData(prev => ({ ...prev, board: { ...prev.board, columns: newCols } }));
            try {
                await api.post('/backlog/columns/reorder', { column_ids: newCols.map(c => c.id) });
            } catch (e) {
                toast.error(formatApiError(e?.response?.data?.detail) || 'Reorder failed');
                load();
            }
            return;
        }

        // CARD drag
        if (destination.droppableId === source.droppableId && destination.index === source.index) return;
        const fromCol = source.droppableId;
        const toCol   = destination.droppableId;
        const fromList = [...(grouped[fromCol] || [])];
        const [moved] = fromList.splice(source.index, 1);
        const toListBase = fromCol === toCol ? fromList : [...(grouped[toCol] || [])];
        // Compute order using cards in the destination column EXCLUDING the moved card
        const newOrder = computeNewOrder(toListBase, destination.index);

        // Optimistic update
        setData(prev => ({
            ...prev,
            cards: prev.cards.map(c => c.id === draggableId
                ? { ...c, column_id: toCol, order: newOrder } : c),
        }));

        try {
            await api.post(`/backlog/cards/${draggableId}/move`, { column_id: toCol, order: newOrder });
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Move failed');
            load();
        }
    };

    const onCardCreated = (card) => {
        setData(prev => ({ ...prev, cards: [...prev.cards, card] }));
    };
    const onCardUpdated = (id, patch) => {
        setData(prev => ({ ...prev, cards: prev.cards.map(c => c.id === id ? { ...c, ...patch } : c) }));
    };
    const onCardDeleted = (id) => {
        setData(prev => ({ ...prev, cards: prev.cards.filter(c => c.id !== id) }));
        setOpenCardId(null);
    };
    const onBoardUpdated = (patch) => {
        setData(prev => ({ ...prev, board: { ...prev.board, ...patch } }));
    };

    // Add a new column
    const addColumn = async () => {
        const name = window.prompt('New column name');
        if (!name?.trim()) return;
        try {
            const r = await api.post('/backlog/columns', { name: name.trim() });
            setData(prev => ({
                ...prev,
                board: { ...prev.board, columns: [...prev.board.columns, r.data.column] },
            }));
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Failed to add column');
        }
    };

    if (loading) {
        return <div className="p-8 text-sm text-[color:var(--bm-text-muted)]">Loading board…</div>;
    }
    if (!data) {
        return <div className="p-8 text-sm text-[color:var(--bm-text-muted)]">Board unavailable.</div>;
    }

    return (
        <div className="flex flex-col h-full" data-testid="backlog-board">
            <PageHeader
                title="Ideas & Backlog"
                description="Capture ideas, plan dev work, and track delivery — drag cards across columns, tag clients, and filter."
                actions={
                    <div className="flex items-center gap-2">
                        <ManageLabelsButton
                            labels={data.board.labels}
                            onChange={(labels) => onBoardUpdated({ labels })}
                            canEdit={canEdit}
                            canDelete={canDelete}
                        />
                        {canEdit && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={addColumn}
                                data-testid="add-column-btn"
                            >
                                <Plus className="w-4 h-4 mr-1" /> Column
                            </Button>
                        )}
                    </div>
                }
            />

            {/* Filter bar */}
            <div className="px-8 py-3 border-b border-[color:var(--bm-border)] bg-white flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[220px] max-w-[360px]">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search cards…"
                        className="pl-8 h-9"
                        data-testid="backlog-search"
                    />
                </div>

                <FilterPill
                    label="Client"
                    icon={Building2}
                    options={data.clients.map(c => ({ value: c.id, label: c.name }))}
                    selected={clientFilter}
                    onChange={setClientFilter}
                    testId="filter-client"
                />
                <FilterPill
                    label="Label"
                    icon={Tag}
                    options={data.board.labels.map(l => ({ value: l.id, label: l.name, swatch: l.color }))}
                    selected={labelFilter}
                    onChange={setLabelFilter}
                    testId="filter-label"
                />
                <FilterPill
                    label="Priority"
                    icon={Filter}
                    options={Object.entries(PRIORITY_META).map(([k, v]) => ({ value: k, label: v.label, swatch: v.dot }))}
                    selected={priorityFilter}
                    onChange={setPriorityFilter}
                    testId="filter-priority"
                />
                <FilterPill
                    label="Assignee"
                    icon={UserIcon}
                    options={data.users.map(u => ({ value: u.id, label: u.name }))}
                    selected={assigneeFilter}
                    onChange={setAssigneeFilter}
                    testId="filter-assignee"
                />

                {filtersActive && (
                    <button
                        className="text-xs text-slate-500 hover:text-slate-700 underline ml-1"
                        onClick={() => {
                            setSearch(''); setClientFilter([]); setLabelFilter([]);
                            setPriorityFilter([]); setAssigneeFilter([]);
                        }}
                        data-testid="clear-filters-btn"
                    >
                        Clear filters
                    </button>
                )}

                <div className="ml-auto text-xs text-slate-500">
                    {data.cards.length} card{data.cards.length === 1 ? '' : 's'}
                </div>
            </div>

            {/* Board */}
            <div className="flex-1 overflow-x-auto overflow-y-hidden bg-[color:var(--bm-bg)]">
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="board" direction="horizontal" type="COLUMN">
                        {(prov) => (
                            <div
                                ref={prov.innerRef}
                                {...prov.droppableProps}
                                className="inline-flex items-start gap-3 p-5 min-h-full"
                            >
                                {data.board.columns.map((col, idx) => (
                                    <Draggable
                                        key={col.id}
                                        draggableId={`col-${col.id}`}
                                        index={idx}
                                        isDragDisabled={!canEdit}
                                    >
                                        {(colProv) => (
                                            <div
                                                ref={colProv.innerRef}
                                                {...colProv.draggableProps}
                                                className="w-[300px] flex-shrink-0 rounded-lg bg-white border border-[color:var(--bm-border)] shadow-sm flex flex-col max-h-[calc(100vh-220px)]"
                                                data-testid={`column-${col.id}`}
                                            >
                                                <ColumnHeader
                                                    column={col}
                                                    dragHandleProps={colProv.dragHandleProps}
                                                    cardCount={(grouped[col.id] || []).length}
                                                    boardColumns={data.board.columns}
                                                    canEdit={canEdit}
                                                    canDelete={canDelete}
                                                    onChange={(patch) => onBoardUpdated({
                                                        columns: data.board.columns.map(c => c.id === col.id ? { ...c, ...patch } : c),
                                                    })}
                                                    onDelete={(newCols) => onBoardUpdated({ columns: newCols })}
                                                    onReload={load}
                                                />

                                                <Droppable droppableId={col.id} type="CARD">
                                                    {(dropProv, snap) => (
                                                        <div
                                                            ref={dropProv.innerRef}
                                                            {...dropProv.droppableProps}
                                                            className={`flex-1 overflow-y-auto px-2 py-2 space-y-2 transition-colors ${snap.isDraggingOver ? 'bg-slate-50' : ''}`}
                                                        >
                                                            {(grouped[col.id] || []).map((card, ci) => (
                                                                <Draggable
                                                                    key={card.id}
                                                                    draggableId={card.id}
                                                                    index={ci}
                                                                    isDragDisabled={!canEdit}
                                                                >
                                                                    {(cardProv) => (
                                                                        <div
                                                                            ref={cardProv.innerRef}
                                                                            {...cardProv.draggableProps}
                                                                            {...cardProv.dragHandleProps}
                                                                        >
                                                                            <CardChip
                                                                                card={card}
                                                                                labelById={labelById}
                                                                                clientById={clientById}
                                                                                userById={userById}
                                                                                onClick={() => setOpenCardId(card.id)}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </Draggable>
                                                            ))}
                                                            {dropProv.placeholder}
                                                        </div>
                                                    )}
                                                </Droppable>

                                                {canCreate && (
                                                    <AddCardInline
                                                        columnId={col.id}
                                                        onCreated={onCardCreated}
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {prov.placeholder}
                                {canEdit && (
                                    <button
                                        onClick={addColumn}
                                        className="w-[300px] flex-shrink-0 rounded-lg border-2 border-dashed border-slate-300 hover:border-slate-400 hover:bg-white text-slate-500 hover:text-slate-700 px-3 py-2 text-sm transition flex items-center justify-center gap-1.5"
                                        data-testid="add-column-inline"
                                    >
                                        <Plus className="w-4 h-4" /> Add column
                                    </button>
                                )}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>

            {openCardId && (
                <CardModal
                    cardId={openCardId}
                    board={data.board}
                    clients={data.clients}
                    users={data.users}
                    onClose={() => setOpenCardId(null)}
                    onUpdated={onCardUpdated}
                    onDeleted={onCardDeleted}
                    canEdit={canEdit}
                    canDelete={canDelete}
                />
            )}
        </div>
    );
}


// ────────────────────────────────────────────────────────────────────────────
// Column header — title, count, color, edit / delete menu
// ────────────────────────────────────────────────────────────────────────────
function ColumnHeader({ column, dragHandleProps, cardCount, boardColumns, canEdit, canDelete, onChange, onReload }) {
    const [editing, setEditing] = useState(false);
    const [name, setName]       = useState(column.name);
    const [color, setColor]     = useState(column.color);
    const [wip, setWip]         = useState(column.wip_limit || 0);
    useEffect(() => { setName(column.name); setColor(column.color); setWip(column.wip_limit || 0); }, [column]);

    const overWip = column.wip_limit > 0 && cardCount > column.wip_limit;

    const save = async () => {
        try {
            await api.patch(`/backlog/columns/${column.id}`, { name, color, wip_limit: Number(wip) || 0 });
            onChange({ name, color, wip_limit: Number(wip) || 0 });
            setEditing(false);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Failed to update column');
        }
    };

    const remove = async () => {
        const others = boardColumns.filter(c => c.id !== column.id);
        if (others.length === 0) { toast.error('Cannot delete the last column'); return; }
        const target = window.confirm(
            `Delete column "${column.name}"?\n\nClick OK to MOVE its ${cardCount} card(s) into the first remaining column, or Cancel to abort.`,
        );
        if (!target) return;
        try {
            await api.delete(`/backlog/columns/${column.id}`, { params: { move_to: others[0].id } });
            onReload();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Failed to delete column');
        }
    };

    return (
        <div
            className="px-3 py-2.5 border-b border-[color:var(--bm-border)] flex items-center gap-2"
            style={{ borderTop: `3px solid ${column.color || '#cbd5e1'}` }}
        >
            <div {...dragHandleProps} className={`text-slate-400 ${canEdit ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'}`}>
                <GripVertical className="w-4 h-4" />
            </div>

            {editing ? (
                <div className="flex-1 flex items-center gap-1">
                    <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="h-7 text-sm flex-1"
                        autoFocus
                        onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
                        data-testid={`column-name-input-${column.id}`}
                    />
                    <Popover>
                        <PopoverTrigger asChild>
                            <button
                                className="w-6 h-6 rounded border border-slate-300 flex-shrink-0"
                                style={{ background: color }}
                                title="Pick color"
                            />
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-2">
                            <ColorPicker value={color} onChange={setColor} />
                        </PopoverContent>
                    </Popover>
                    <Input
                        value={wip || ''}
                        onChange={(e) => setWip(e.target.value)}
                        placeholder="WIP"
                        className="w-14 h-7 text-xs"
                        title="WIP limit (0 = none)"
                    />
                    <Button size="sm" className="h-7 px-2" onClick={save}>Save</Button>
                </div>
            ) : (
                <>
                    <div className="flex-1 min-w-0 flex items-center gap-1.5">
                        <span className="text-[13px] font-semibold text-slate-800 truncate">{column.name}</span>
                        <span className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${overWip ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'}`}>
                            {cardCount}{column.wip_limit > 0 ? `/${column.wip_limit}` : ''}
                        </span>
                    </div>
                    {canEdit && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button className="p-1 rounded hover:bg-slate-100 text-slate-500" data-testid={`column-menu-${column.id}`}>
                                    <MoreVertical className="w-4 h-4" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => setEditing(true)}>
                                    <Pencil className="w-3.5 h-3.5 mr-2" /> Rename / style
                                </DropdownMenuItem>
                                {canDelete && (
                                    <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={remove} className="text-rose-600 focus:text-rose-700">
                                            <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete column
                                        </DropdownMenuItem>
                                    </>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </>
            )}
        </div>
    );
}


function ColorPicker({ value, onChange }) {
    return (
        <div className="grid grid-cols-8 gap-1.5">
            {SWATCHES.map(c => (
                <button
                    key={c}
                    onClick={() => onChange(c)}
                    className={`w-6 h-6 rounded border-2 ${value === c ? 'border-slate-900 scale-110' : 'border-white'} hover:scale-110 transition`}
                    style={{ background: c }}
                    aria-label={`Color ${c}`}
                />
            ))}
        </div>
    );
}


// ────────────────────────────────────────────────────────────────────────────
// Card chip (small card preview shown on board)
// ────────────────────────────────────────────────────────────────────────────
function CardChip({ card, labelById, clientById, userById, onClick }) {
    const labels   = card.label_ids.map(id => labelById[id]).filter(Boolean);
    const clients  = card.client_ids.map(id => clientById[id]).filter(Boolean);
    const checked  = card.checklist?.filter(i => i.done).length || 0;
    const total    = card.checklist?.length || 0;
    const comments = card.comments?.length || 0;
    const atts     = card.attachments?.length || 0;
    const prio     = PRIORITY_META[card.priority] || PRIORITY_META.medium;
    const assignees = card.assignee_ids.map(id => userById[id]).filter(Boolean);

    return (
        <button
            onClick={onClick}
            className="block w-full text-left rounded-md bg-white border border-[color:var(--bm-border)] hover:border-slate-400 hover:shadow-sm transition p-2.5"
            data-testid={`card-${card.id}`}
        >
            {labels.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-1.5">
                    {labels.map(l => (
                        <span
                            key={l.id}
                            className="text-[10px] font-medium px-1.5 py-0.5 rounded text-white"
                            style={{ background: l.color }}
                            title={l.name}
                        >
                            {l.name}
                        </span>
                    ))}
                </div>
            )}
            <div className="text-[13px] font-medium text-slate-800 leading-snug">{card.title}</div>
            {clients.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                    {clients.map(c => (
                        <span
                            key={c.id}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 inline-flex items-center gap-1"
                            title={c.name}
                        >
                            <Building2 className="w-2.5 h-2.5" />
                            <span className="max-w-[90px] truncate">{c.name}</span>
                        </span>
                    ))}
                </div>
            )}
            <div className="flex items-center justify-between mt-2 text-[11px] text-slate-500">
                <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1" title={`Priority: ${prio.label}`}>
                        <span className="w-2 h-2 rounded-full" style={{ background: prio.dot }} />
                        {prio.label}
                    </span>
                    {card.due_date && (
                        <span className="inline-flex items-center gap-0.5" title={`Due ${card.due_date}`}>
                            <Calendar className="w-3 h-3" /> {fmtDueShort(card.due_date)}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {total > 0 && (
                        <span className="inline-flex items-center gap-0.5" title="Checklist">
                            <CheckSquare className="w-3 h-3" /> {checked}/{total}
                        </span>
                    )}
                    {comments > 0 && (
                        <span className="inline-flex items-center gap-0.5" title="Comments">
                            <MessageSquare className="w-3 h-3" /> {comments}
                        </span>
                    )}
                    {atts > 0 && (
                        <span className="inline-flex items-center gap-0.5" title="Attachments">
                            <Paperclip className="w-3 h-3" /> {atts}
                        </span>
                    )}
                    {assignees.length > 0 && (
                        <div className="flex -space-x-1">
                            {assignees.slice(0, 3).map(u => (
                                <span
                                    key={u.id}
                                    className="w-4 h-4 rounded-full bg-slate-700 text-white text-[8px] font-semibold flex items-center justify-center ring-1 ring-white"
                                    title={u.name}
                                >
                                    {(u.name || '?').charAt(0).toUpperCase()}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </button>
    );
}

function fmtDueShort(iso) {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
    } catch { return iso; }
}


// ────────────────────────────────────────────────────────────────────────────
// Add-card inline (footer of each column)
// ────────────────────────────────────────────────────────────────────────────
function AddCardInline({ columnId, onCreated }) {
    const [adding, setAdding] = useState(false);
    const [title, setTitle]   = useState('');
    const inputRef = useRef(null);
    useEffect(() => { if (adding) inputRef.current?.focus(); }, [adding]);

    const submit = async () => {
        const t = title.trim();
        if (!t) { setAdding(false); return; }
        try {
            const r = await api.post('/backlog/cards', { column_id: columnId, title: t, priority: 'medium' });
            onCreated(r.data.card);
            setTitle('');
            setAdding(true);    // keep open for rapid entry
            inputRef.current?.focus();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Failed to add card');
        }
    };

    if (!adding) {
        return (
            <button
                onClick={() => setAdding(true)}
                className="m-2 px-2 py-1.5 text-[12.5px] text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded inline-flex items-center gap-1.5 transition"
                data-testid={`add-card-btn-${columnId}`}
            >
                <Plus className="w-3.5 h-3.5" /> Add a card
            </button>
        );
    }
    return (
        <div className="p-2 border-t border-[color:var(--bm-border)]">
            <textarea
                ref={inputRef}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Title…"
                className="w-full text-[13px] p-2 rounded border border-slate-300 focus:outline-none focus:border-slate-500 resize-none"
                rows={2}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
                    if (e.key === 'Escape') { setAdding(false); setTitle(''); }
                }}
                data-testid={`add-card-input-${columnId}`}
            />
            <div className="flex items-center gap-1 mt-1.5">
                <Button size="sm" className="h-7 px-3" onClick={submit} data-testid={`add-card-submit-${columnId}`}>
                    Add card
                </Button>
                <button
                    onClick={() => { setAdding(false); setTitle(''); }}
                    className="p-1 rounded hover:bg-slate-100 text-slate-500"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}


// ────────────────────────────────────────────────────────────────────────────
// Filter pill (multi-select dropdown)
// ────────────────────────────────────────────────────────────────────────────
function FilterPill({ label, icon: Icon, options, selected, onChange, testId }) {
    const [query, setQuery] = useState('');
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return options.filter(o => o.label.toLowerCase().includes(q));
    }, [options, query]);
    const toggle = (v) =>
        onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
    const active = selected.length > 0;
    return (
        <Popover onOpenChange={(o) => { if (!o) setQuery(''); }}>
            <PopoverTrigger asChild>
                <button
                    className={`inline-flex items-center gap-1.5 px-2.5 h-9 rounded-md text-[12.5px] border transition ${
                        active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'
                    }`}
                    data-testid={testId}
                >
                    <Icon className="w-3.5 h-3.5" />
                    {label}
                    {active && <span className="ml-0.5 text-[10px] font-semibold px-1 rounded bg-white/20">{selected.length}</span>}
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-60 p-2" align="start">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold px-1 pb-1">{label}</div>
                {options.length === 0 ? (
                    <div className="px-2 py-3 text-xs text-slate-400">None available</div>
                ) : (
                    <>
                        {options.length > 6 && (
                            <div className="relative mb-1">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                                <Input
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder={`Search ${label.toLowerCase()}…`}
                                    className="h-7 text-[12.5px] pl-7"
                                    autoFocus
                                    data-testid={`${testId}-search`}
                                />
                            </div>
                        )}
                        <div className="max-h-64 overflow-y-auto">
                            {filtered.length === 0 ? (
                                <div className="px-2 py-3 text-xs text-slate-400">No matches</div>
                            ) : filtered.map(opt => (
                                <button
                                    key={opt.value}
                                    onClick={() => toggle(opt.value)}
                                    className={`w-full text-left text-[13px] px-2 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2 ${
                                        selected.includes(opt.value) ? 'bg-slate-100 font-medium' : ''
                                    }`}
                                >
                                    {opt.swatch && <span className="w-3 h-3 rounded" style={{ background: opt.swatch }} />}
                                    <span className="flex-1 truncate">{opt.label}</span>
                                    {selected.includes(opt.value) && <span className="text-slate-900">✓</span>}
                                </button>
                            ))}
                        </div>
                    </>
                )}
                {selected.length > 0 && (
                    <button
                        className="w-full mt-1 text-[12px] text-slate-500 hover:text-slate-700 py-1"
                        onClick={() => onChange([])}
                    >
                        Clear
                    </button>
                )}
            </PopoverContent>
        </Popover>
    );
}


// ────────────────────────────────────────────────────────────────────────────
// Manage Labels (add / rename / recolor / delete)
// ────────────────────────────────────────────────────────────────────────────
function ManageLabelsButton({ labels, onChange, canEdit, canDelete }) {
    const [open, setOpen] = useState(false);
    const [newName, setNewName] = useState('');
    const [newColor, setNewColor] = useState('#3b82f6');

    const addLabel = async () => {
        const n = newName.trim();
        if (!n) return;
        try {
            const r = await api.post('/backlog/labels', { name: n, color: newColor });
            onChange([...labels, r.data.label]);
            setNewName(''); setNewColor('#3b82f6');
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Failed to add label');
        }
    };
    const updateLabel = async (lab, patch) => {
        try {
            await api.patch(`/backlog/labels/${lab.id}`, patch);
            onChange(labels.map(l => l.id === lab.id ? { ...l, ...patch } : l));
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Failed to update label');
        }
    };
    const removeLabel = async (lab) => {
        if (!window.confirm(`Delete label "${lab.name}"? It will be removed from all cards.`)) return;
        try {
            await api.delete(`/backlog/labels/${lab.id}`);
            onChange(labels.filter(l => l.id !== lab.id));
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Failed to delete label');
        }
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid="manage-labels-btn">
                    <Tag className="w-4 h-4 mr-1" /> Labels
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-3" align="end">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">Manage labels</div>
                <div className="max-h-64 overflow-y-auto space-y-1.5">
                    {labels.map(l => (
                        <div key={l.id} className="flex items-center gap-1.5">
                            <Popover>
                                <PopoverTrigger asChild>
                                    <button
                                        className="w-6 h-6 rounded border border-slate-300 flex-shrink-0"
                                        style={{ background: l.color }}
                                        title="Change colour"
                                        disabled={!canEdit}
                                    />
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-2">
                                    <ColorPicker value={l.color} onChange={(c) => updateLabel(l, { color: c })} />
                                </PopoverContent>
                            </Popover>
                            <Input
                                value={l.name}
                                onChange={(e) => onChange(labels.map(x => x.id === l.id ? { ...x, name: e.target.value } : x))}
                                onBlur={(e) => { if (e.target.value !== l.name) updateLabel(l, { name: e.target.value }); }}
                                className="h-7 text-sm flex-1"
                                disabled={!canEdit}
                            />
                            {canDelete && (
                                <button
                                    onClick={() => removeLabel(l)}
                                    className="p-1 text-slate-400 hover:text-rose-600"
                                    title="Delete label"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
                {canEdit && (
                    <div className="border-t border-slate-200 mt-2 pt-2 flex items-center gap-1.5">
                        <Popover>
                            <PopoverTrigger asChild>
                                <button
                                    className="w-6 h-6 rounded border border-slate-300 flex-shrink-0"
                                    style={{ background: newColor }}
                                    title="Pick colour"
                                />
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-2">
                                <ColorPicker value={newColor} onChange={setNewColor} />
                            </PopoverContent>
                        </Popover>
                        <Input
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            placeholder="New label name"
                            className="h-7 text-sm flex-1"
                            onKeyDown={(e) => { if (e.key === 'Enter') addLabel(); }}
                            data-testid="new-label-input"
                        />
                        <Button size="sm" className="h-7 px-2" onClick={addLabel} data-testid="new-label-add">
                            <Plus className="w-3.5 h-3.5" />
                        </Button>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
}
